/**
 * ColumnLayoutEditor — rename / show-hide / reorder / add custom columns for one
 * document type (daily site invoice, job invoice, estimate). Pure controlled component:
 * `value` is the column array (or null = "use default"), `onChange(nextArray|null)`.
 */
import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { GripVertical, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';

export const ColumnLayoutEditor = ({ docType, value, defaults, sources, onChange, testIdPrefix = 'cols', compact = false }) => {
  const cols = value || defaults || [];
  const [dragIdx, setDragIdx] = useState(null);
  const srcMap = Object.fromEntries((sources || []).map((s) => [s.key, s]));

  const update = (i, patch) => onChange(cols.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const move = (from, to) => {
    if (to < 0 || to >= cols.length || from === to) return;
    const next = [...cols];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const remove = (i) => onChange(cols.filter((_, j) => j !== i));
  const addCustom = () => {
    const n = cols.filter((c) => c.source === 'custom').length + 1;
    onChange([...cols, { key: `custom_${Date.now().toString(36)}`, label: `Custom ${n}`, source: 'custom', visible: true, align: 'left' }]);
  };
  const addSource = (srcKey) => {
    const s = srcMap[srcKey];
    if (!s) return;
    let key = srcKey;
    if (cols.some((c) => c.key === key)) key = `${srcKey}_${Date.now().toString(36)}`;
    onChange([...cols, { key, label: s.label, source: srcKey, visible: true, align: s.align }]);
  };
  const unused = (sources || []).filter((s) => s.key !== 'custom' && !cols.some((c) => c.source === s.key));
  const grid = compact ? 'grid-cols-[20px_1fr_1fr] gap-x-2 gap-y-1.5' : 'grid-cols-[24px_1fr_1.2fr_90px_70px_110px] gap-2';

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-${docType}`}>
      {!compact && (
        <div className={`grid ${grid} text-[10px] uppercase tracking-wide text-slate-500 px-1`}>
          <span /><span>Header label</span><span>Data shown</span><span>Align</span><span>Show</span><span />
        </div>
      )}
      {cols.map((c, i) => (
        <div
          key={c.key}
          draggable
          onDragStart={() => setDragIdx(i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => { if (dragIdx !== null) move(dragIdx, i); setDragIdx(null); }}
          className={`grid ${grid} items-center rounded-md border px-1 py-1.5 ${c.visible ? 'border-slate-700 bg-slate-800/40' : 'border-slate-800 opacity-60'}`}
          data-testid={`${testIdPrefix}-${docType}-row-${i}`}
        >
          <GripVertical className="w-4 h-4 text-slate-500 cursor-grab" />
          <Input value={c.label} maxLength={40} onChange={(e) => update(i, { label: e.target.value })} className="h-8 text-sm" data-testid={`${testIdPrefix}-${docType}-label-${i}`} />
          <Select value={c.source} onValueChange={(v) => update(i, { source: v, align: srcMap[v]?.align || c.align })}>
            <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-${docType}-source-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {(sources || []).map((s) => <SelectItem key={s.key} value={s.key}><span className="text-xs">{s.label}</span><span className="text-[10px] text-slate-400 ml-2">{s.description}</span></SelectItem>)}
            </SelectContent>
          </Select>
          {compact && <span />}
          <div className={compact ? 'col-span-2 flex items-center gap-2 flex-wrap' : 'contents'}>
          <Select value={c.align || 'left'} onValueChange={(v) => update(i, { align: v })}>
            <SelectTrigger className={`h-8 text-xs ${compact ? 'w-24' : ''}`} data-testid={`${testIdPrefix}-${docType}-align-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <Switch checked={c.visible !== false} onCheckedChange={(v) => update(i, { visible: v })} data-testid={`${testIdPrefix}-${docType}-visible-${i}`} />
            {compact && 'Show'}
          </label>
          <div className={`flex items-center gap-0.5 ${compact ? 'ml-auto' : 'justify-end'}`}>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`${testIdPrefix}-${docType}-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, i + 1)} disabled={i === cols.length - 1} data-testid={`${testIdPrefix}-${docType}-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => remove(i)} disabled={cols.length <= 1} data-testid={`${testIdPrefix}-${docType}-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={addCustom} data-testid={`${testIdPrefix}-${docType}-add-custom`}><Plus className="w-3.5 h-3.5 mr-1" /> Custom text column</Button>
        {unused.length > 0 && (
          <Select value="" onValueChange={addSource}>
            <SelectTrigger className="h-8 w-56 text-xs" data-testid={`${testIdPrefix}-${docType}-add-source`}><SelectValue placeholder="Add a data column…" /></SelectTrigger>
            <SelectContent>{unused.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} <span className="text-[10px] text-slate-400 ml-1">{s.description}</span></SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button size="sm" variant="ghost" className="ml-auto text-slate-400" onClick={() => onChange(null)} data-testid={`${testIdPrefix}-${docType}-reset`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default</Button>
      </div>
      <p className="text-[11px] text-slate-500">Drag rows (or use the arrows) to reorder. Custom text columns are typed per line when you build the document. Headers, on-screen preview, PDF and CSV all use these labels.</p>
    </div>
  );
};

/** One input per visible custom column — drop into any line editor. */
export const CustomColumnInputs = ({ columns, values, onChange, testIdPrefix = 'cf', compact = false }) => {
  const customs = (columns || []).filter((c) => c.source === 'custom' && c.visible !== false);
  if (!customs.length) return null;
  return (
    <div className={compact ? 'flex flex-wrap gap-2' : 'grid grid-cols-2 md:grid-cols-3 gap-2 mt-1'}>
      {customs.map((c) => (
        <div key={c.key} className={compact ? 'w-40' : ''}>
          {!compact && <label className="text-[10px] uppercase tracking-wide text-slate-500">{c.label}</label>}
          <Input
            value={(values || {})[c.key] || ''}
            maxLength={200}
            placeholder={c.label}
            onChange={(e) => onChange({ ...(values || {}), [c.key]: e.target.value })}
            className="h-8 text-xs"
            data-testid={`${testIdPrefix}-${c.key}`}
          />
        </div>
      ))}
    </div>
  );
};

export default ColumnLayoutEditor;
